import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Section from '../components/common/Section';
import { AnalyticsSummaryPanel, type AnalyticsSummary } from '../components/AnalyticsSummaryPanel';
import { SECTOR_OPTIONS, REGION_OPTIONS, EMPLOYEE_BAND_OPTIONS } from '../constants/miniEsgMeta';
import { buildMiniEsgAuthHeaders, getMiniEsgApiBase } from '../utils/miniEsgApi';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useToastStore } from '../store/toastStore';

type ReportListEntry = {
  id: string;
  companyName: string;
  taxId: string;
  score: { total: number; level: string; environment: number; social: number; governance: number };
  sector?: string;
  region?: string;
  employeeBand?: string;
  createdAt: number;
};

const DATE_PRESETS = [
  { key: '30', label: 'Last 30 days', days: 30 },
  { key: '90', label: 'Last 90 days', days: 90 },
  { key: '365', label: 'Last year', days: 365 },
];

export function PremiumAnalyticsRoute() {
  const API_BASE = useMemo(() => getMiniEsgApiBase(), []);
  const { showToast } = useToastStore();
  const [filters, setFilters] = useState<{ sector?: string; region?: string; employeeBand?: string; from?: number }>({});
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [reports, setReports] = useState<ReportListEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drilldownReport, setDrilldownReport] = useState<ReportListEntry | null>(null);

  const fetchSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filters.sector) params.append('sector', filters.sector);
      if (filters.region) params.append('region', filters.region);
      if (filters.employeeBand) params.append('employeeBand', filters.employeeBand);
      if (filters.from) params.append('from', filters.from.toString());
      const response = await fetch(`${API_BASE}/analytics/summary${params.toString() ? `?${params.toString()}` : ''}`, {
        headers: buildMiniEsgAuthHeaders(),
      });
      if (!response.ok) throw new Error(await response.text());
      const data = await response.json();
      setSummary(data);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to load analytics';
      setError(message);
      showToast(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchReports = async () => {
    setReportsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/reports`, { headers: buildMiniEsgAuthHeaders() });
      if (!response.ok) throw new Error(await response.text());
      const json = await response.json();
      setReports(json?.reports || []);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to load reports';
      showToast(message, 'error');
    } finally {
      setReportsLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
    fetchReports();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const topCompanies = useMemo(() => {
    return [...reports]
      .sort((a, b) => (b.score?.total ?? 0) - (a.score?.total ?? 0))
      .slice(0, 8);
  }, [reports]);

  const handlePreset = (days: number) => {
    const from = Date.now() - days * 24 * 60 * 60 * 1000;
    setFilters((prev) => ({ ...prev, from }));
  };

  const trendPoints = summary?.trend ?? [];

  const handleExportCSV = useCallback(() => {
    if (!summary) {
      showToast('Run analytics first', 'warning');
      return;
    }
    const rows = [
      ['Metric', 'Environment', 'Social', 'Governance', 'Total'],
      ['Average', summary.averages.environment, summary.averages.social, summary.averages.governance, summary.averages.total],
    ];
    const csvContent = rows.map((row) => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-summary-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showToast('CSV exported', 'success');
  }, [summary, showToast]);

  const handleExportPNG = useCallback(() => {
    showToast('Use browser print/save-as to capture the dashboard.', 'info');
    window.print();
  }, [showToast]);

  const handleExportPDF = useCallback(() => {
    if (!summary) {
      showToast('Run analytics first', 'warning');
      return;
    }
    const printable = window.open('', 'printable');
    if (!printable) {
      showToast('Popup blocked. Allow popups to export.', 'error');
      return;
    }
    printable.document.write('<html><head><title>Analytics Summary</title></head><body>');
    printable.document.write('<h2>Analytics Summary</h2>');
    printable.document.write('<table border="1" cellspacing="0" cellpadding="6"><thead><tr><th>Metric</th><th>Value</th></tr></thead><tbody>');
    printable.document.write(`<tr><td>Environment Average</td><td>${summary.averages.environment}</td></tr>`);
    printable.document.write(`<tr><td>Social Average</td><td>${summary.averages.social}</td></tr>`);
    printable.document.write(`<tr><td>Governance Average</td><td>${summary.averages.governance}</td></tr>`);
    printable.document.write(`<tr><td>Total Average</td><td>${summary.averages.total}</td></tr>`);
    printable.document.write('</tbody></table>');
    printable.document.write('</body></html>');
    printable.document.close();
    printable.focus();
    printable.print();
    printable.close();
    showToast('Browser print dialog opened. Save as PDF.', 'info');
  }, [summary, showToast]);

  return (
    <Section title="Premium Analytics">
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <FilterSelect
            label="Sector"
            value={filters.sector || ''}
            onChange={(value) => setFilters((prev) => ({ ...prev, sector: value || undefined }))}
            options={SECTOR_OPTIONS}
          />
          <FilterSelect
            label="Region"
            value={filters.region || ''}
            onChange={(value) => setFilters((prev) => ({ ...prev, region: value || undefined }))}
            options={REGION_OPTIONS}
          />
          <FilterSelect
            label="Employee Band"
            value={filters.employeeBand || ''}
            onChange={(value) => setFilters((prev) => ({ ...prev, employeeBand: value || undefined }))}
            options={EMPLOYEE_BAND_OPTIONS}
          />
          <div>
            <label className="block text-xs uppercase tracking-wide text-neutral-500 mb-1">Date range</label>
            <div className="flex gap-2 flex-wrap">
              {DATE_PRESETS.map((preset) => (
                <button
                  key={preset.key}
                  className={`px-3 py-1 rounded border text-xs ${
                    filters.from && Date.now() - filters.from <= preset.days * 24 * 60 * 60 * 1000
                      ? 'border-evat-neon text-evat-neon'
                      : 'border-neutral-700 text-neutral-400'
                  }`}
                  onClick={() => handlePreset(preset.days)}
                >
                  {preset.label}
                </button>
              ))}
              <button
                className="text-xs text-neutral-400 underline"
                onClick={() => setFilters((prev) => ({ ...prev, from: undefined }))}
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={fetchSummary}
            className="px-4 py-2 bg-evat-neon/20 border border-evat-neon text-evat-neon rounded text-sm"
            disabled={loading}
          >
            {loading ? 'Loading…' : 'Refresh Analytics'}
          </button>
          <button
            onClick={fetchReports}
            className="px-4 py-2 bg-neutral-900 border border-neutral-700 text-sm rounded text-neutral-200"
            disabled={reportsLoading}
          >
            {reportsLoading ? 'Syncing…' : 'Sync Reports'}
          </button>
          <button
            onClick={handleExportCSV}
            className="px-4 py-2 bg-neutral-900 border border-neutral-700 text-sm rounded text-neutral-200"
          >
            Export CSV
          </button>
          <button
            onClick={handleExportPNG}
            className="px-4 py-2 bg-neutral-900 border border-neutral-700 text-sm rounded text-neutral-200"
          >
            Export Image
          </button>
          <button
            onClick={handleExportPDF}
            className="px-4 py-2 bg-neutral-900 border border-neutral-700 text-sm rounded text-neutral-200"
          >
            Export PDF
          </button>
        </div>

        {error && <div className="text-sm text-red-400">{error}</div>}

        {loading && !summary && <LoadingSpinner />}

        {summary && <AnalyticsSummaryPanel summary={summary} />}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <TrendChart points={trendPoints} />
          <BenchmarkTable benchmarks={summary?.benchmarks || []} />
        </div>

        <div>
          <h3 className="text-base font-orbitron text-evat-gold mb-2">Top Companies</h3>
          <div className="border border-evat-gold/20 rounded-lg divide-y divide-neutral-800">
            {topCompanies.length === 0 && (
              <div className="p-4 text-sm text-neutral-500">No reports found.</div>
            )}
            {topCompanies.map((company) => (
              <div key={company.id} className="p-3 flex items-center justify-between">
                <div>
                  <div className="text-sm text-neutral-200">{company.companyName || 'Unknown'}</div>
                  <div className="text-xs text-neutral-500">
                    {company.sector || 'Any sector'} · {company.region || 'Any region'}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-xs px-2 py-0.5 rounded ${
                      company.score?.level === 'red'
                        ? 'bg-red-900/40 text-red-300'
                        : company.score?.level === 'yellow'
                          ? 'bg-yellow-900/40 text-yellow-200'
                          : 'bg-green-900/40 text-green-300'
                    }`}
                  >
                    {company.score?.total ?? '--'} / 30
                  </span>
                  <span className="text-xs text-neutral-500">
                    {new Date(company.createdAt).toLocaleDateString()}
                  </span>
                  <button
                    onClick={() => setDrilldownReport(company)}
                    className="text-xs text-evat-neon border border-evat-neon/50 rounded px-2 py-0.5"
                  >
                    Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {drilldownReport && (
          <div className="border border-evat-neon/30 rounded-lg p-4 bg-black/40">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="text-lg font-orbitron text-evat-neon">{drilldownReport.companyName || 'Company details'}</h4>
                <p className="text-xs text-neutral-400">{drilldownReport.taxId} • {drilldownReport.sector || 'Sector NA'} • {drilldownReport.region || 'Region NA'}</p>
              </div>
              <button onClick={() => setDrilldownReport(null)} className="text-xs text-neutral-400 hover:text-neutral-200">✕ Close</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="border border-neutral-800 rounded p-3">
                <div className="text-xs uppercase tracking-wide text-neutral-500 mb-1">Scores</div>
                <ul className="space-y-1 text-neutral-200">
                  <li>Environment: {drilldownReport.score?.environment ?? '--'}</li>
                  <li>Social: {drilldownReport.score?.social ?? '--'}</li>
                  <li>Governance: {drilldownReport.score?.governance ?? '--'}</li>
                  <li>Total: {drilldownReport.score?.total ?? '--'}</li>
                </ul>
              </div>
              <div className="border border-neutral-800 rounded p-3">
                <div className="text-xs uppercase tracking-wide text-neutral-500 mb-1">Metadata</div>
                <ul className="space-y-1 text-neutral-200">
                  <li>Employee band: {drilldownReport.employeeBand || 'n/a'}</li>
                  <li>Created: {new Date(drilldownReport.createdAt).toLocaleString()}</li>
                  <li>Report ID: {drilldownReport.id}</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </Section>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wide text-neutral-500 mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 bg-black border border-neutral-700 rounded text-sm text-neutral-100 focus:border-evat-neon focus:outline-none"
      >
        {options.map((opt) => (
          <option key={opt.value || 'none'} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function TrendChart({ points }: { points: Array<{ date: number; total: number; level: string }> }) {
  if (!points.length) {
    return (
      <div className="border border-neutral-800 rounded-lg p-4 text-sm text-neutral-500">
        Trend data will appear once there are multiple reports.
      </div>
    );
  }
  const width = 480;
  const height = 220;
  const padding = 32;
  const xs = points.map((p) => p.date);
  const ys = points.map((p) => p.total);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = 0;
  const maxY = 30;
  const xScale = (value: number) => padding + ((value - minX) / Math.max(1, maxX - minX)) * (width - padding * 2);
  const yScale = (value: number) => height - padding - ((value - minY) / (maxY - minY)) * (height - padding * 2);
  const sortedPoints = [...points].sort((a, b) => a.date - b.date);
  const linePoints = sortedPoints.map((p) => `${xScale(p.date)},${yScale(p.total)}`).join(' ');
  return (
    <div className="border border-neutral-800 rounded-lg p-4 bg-black/40">
      <div className="text-sm text-neutral-300 mb-2">Score Trend</div>
      <svg width="100%" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Score trend chart">
        <polyline fill="none" stroke="#00ff99" strokeWidth={2} points={linePoints} />
        {sortedPoints.map((p, idx) => (
          <circle key={idx} cx={xScale(p.date)} cy={yScale(p.total)} r={3} fill="#00ff99" />
        ))}
        <text x={padding} y={padding / 2} fill="#888" fontSize={10}>
          {new Date(minX).toLocaleDateString()}
        </text>
        <text x={width - padding} y={padding / 2} textAnchor="end" fill="#888" fontSize={10}>
          {new Date(maxX).toLocaleDateString()}
        </text>
      </svg>
    </div>
  );
}

function BenchmarkTable({ benchmarks }: { benchmarks: Array<{ group: string; average: number; count: number }> }) {
  return (
    <div className="border border-neutral-800 rounded-lg p-4 bg-black/40 h-full">
      <div className="text-sm text-neutral-300 mb-2">Benchmarks</div>
      {benchmarks.length === 0 ? (
        <div className="text-sm text-neutral-500">No benchmarks available yet.</div>
      ) : (
        <table className="w-full text-sm text-neutral-200">
          <thead>
            <tr className="text-xs text-neutral-500 uppercase tracking-wide">
              <th className="text-left py-1">Group</th>
              <th className="text-right py-1">Avg Score</th>
              <th className="text-right py-1">Samples</th>
            </tr>
          </thead>
          <tbody>
            {benchmarks.map((bench) => (
              <tr key={bench.group} className="border-t border-neutral-800">
                <td className="py-1">{bench.group}</td>
                <td className="py-1 text-right">{bench.average}</td>
                <td className="py-1 text-right">{bench.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default PremiumAnalyticsRoute;

